// filepath: apps/web/src/features/directory/employment-timeline/EmploymentTimelinePage.tsx
//
// FR-UI-DIR-1 AC-5 — employment timeline page for a single person.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { usePersonEmployment } from "../queries";

export interface EmploymentTimelinePageProps {
  personId: string;
}

export function EmploymentTimelinePage({
  personId,
}: EmploymentTimelinePageProps): React.ReactElement {
  const { t } = useTranslation();
  const query = usePersonEmployment(personId);

  switch (query.state) {
    case "loading":
      return (
        <p
          className="directory-employment__loading"
          data-testid="employment-loading"
        >
          {t("directory.employment.state.loading")}
        </p>
      );
    case "permission-denied":
      return (
        <p
          role="alert"
          className="directory-employment__permission-denied"
          data-testid="employment-permission-denied"
        >
          {t("directory.employment.state.permission_denied")}
        </p>
      );
    case "fetch-failure":
      return (
        <p
          role="alert"
          className="directory-employment__fetch-failure"
          data-testid="employment-fetch-failure"
        >
          {t("directory.employment.state.fetch_failure")}
        </p>
      );
    case "validation-error":
      return (
        <p
          role="alert"
          className="directory-employment__validation-error"
          data-testid="employment-validation-error"
        >
          {query.error?.message ??
            t("directory.employment.state.validation_error")}
        </p>
      );
    case "rate-limit":
      return (
        <p
          role="alert"
          className="directory-employment__rate-limit"
          data-testid="employment-rate-limit"
        >
          {t("directory.employment.state.rate_limit")}
        </p>
      );
    case "success": {
      const data = query.data;
      if (data === null) {
        return (
          <p
            role="alert"
            className="directory-employment__fetch-failure"
            data-testid="employment-fetch-failure"
          >
            {t("directory.employment.state.fetch_failure")}
          </p>
        );
      }
      const entries = data.items ?? [];
      return (
        <section
          className="directory-employment"
          data-testid="employment-page"
          data-person-id={personId}
        >
          <header className="directory-employment__header">
            <h1>{t("directory.employment.title", "Employment")}</h1>
          </header>
          {entries.length === 0 ? (
            <p
              className="directory-employment__empty"
              data-testid="employment-empty"
            >
              {t("directory.employment.empty")}
            </p>
          ) : (
            <ol
              className="directory-employment__timeline"
              data-testid="employment-timeline"
            >
              {entries.map((e) => (
                <li
                  key={e.id}
                  className="directory-employment__entry"
                  data-employment-id={e.id}
                >
                  <span className="directory-employment__entry-title">
                    {e.position_title}
                  </span>
                  {e.unit_name ? (
                    <span className="directory-employment__entry-unit">
                      {e.unit_name}
                    </span>
                  ) : null}
                  <span className="directory-employment__entry-dates">
                    {e.start_date ?? "?"} → {e.end_date ?? "present"}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      );
    }
  }
}
